import { Box, LinearProgress } from '@mui/material'
import React, { Suspense, useMemo } from 'react'
import { Outlet } from 'react-router'
import { Breadcrumbs } from '@/components/Breadcrumbs/Breadcrumbs'
import { GlobalEntitySelector } from '@/components/GlobalEntitySelector/GlobalEntitySelector'
import {
  IMPERSONATION_BANNER_HEIGHT,
  ImpersonationBanner,
} from '@/components/ImpersonationBanner/ImpersonationBanner'
import { Navbar } from '@/components/Navbar/Navbar'
import { QuickActions } from '@/components/QuickActions/QuickActions'
import { SubNavbar } from '@/components/SubNavbar/SubNavbar'
import { WebPushRegistration } from '@/components/WebPushRegistration/WebPushRegistration'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { useChromeCollapsed } from '@/hooks/useChromeCollapse'
import { useAuthContext, useViewContext } from '@/providers/Global'
import { hasBaseRole } from '@/utils/authorities'

//	///////////////////////
//	MARK: EXPORTS
const LayoutIndexLoggedInInternal: React.FC = () => {
  const Auth = useAuthContext()
  const View = useViewContext()

  const [isDesktop] = useBreakpoint('md')

  const chromeCollapsed = useChromeCollapsed()

  const isImpersonating = Boolean(Auth.active?.impersonation)

  const marginTop = useMemo<string>(() => {
    const bannerOffset = isImpersonating ? IMPERSONATION_BANNER_HEIGHT : 0

    if (chromeCollapsed) {
      return `${bannerOffset}px`
    }

    return `${bannerOffset + View.navbar.height + View.subNavbar.height}px`
  }, [chromeCollapsed, isImpersonating, View.navbar.height, View.subNavbar.height])

  const marginBottom = useMemo<string>(() => {
    if (chromeCollapsed) {
      return '0px'
    }

    return `${View.quickActions.height + (isDesktop ? View.bottomBar.height / 2 : 0)}px`
  }, [chromeCollapsed, View.bottomBar.height, View.quickActions.height, isDesktop])

  const baseRole = hasBaseRole(Auth.active?.roles)

  // L'entité globale ne sert qu'à la compta et aux dépenses : un compte externe s'en passe (et /api/entity lui est fermé).
  if (baseRole && !Auth.active!.globalEntity) {
    return (
      <>
        <WebPushRegistration />
        <GlobalEntitySelector />
      </>
    )
  }

  return (
    <>
      {baseRole ? <WebPushRegistration /> : null}
      <ImpersonationBanner />
      <Navbar />
      <SubNavbar />
      <QuickActions />
      <Box
        p={
          chromeCollapsed
            ? 0.5
            : {
                md: 2,
                xs: 1,
              }
        }
        marginTop={marginTop}
        marginBottom={marginBottom}
      >
        <Breadcrumbs />
        <Suspense fallback={<LinearProgress />}>
          <Outlet />
        </Suspense>
      </Box>
    </>
  )
}

export const LayoutIndexLoggedIn = React.memo(
  LayoutIndexLoggedInInternal,
) as typeof LayoutIndexLoggedInInternal
